import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
} from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { AISearchFilledIcon } from '~assets/icons'
import {
  Avatar,
  Button,
  ContactList,
  Icon,
  IconButton,
  KnownChip,
  type KnownChipType,
  LicenseStatusChip,
  Search,
  type SemanticIconName,
  StatusChip,
  type StatusChipType,
  Typography,
  caseStatusConfig,
} from '~components/internal'
import { BASE_URL_WITH_REACT } from '~config'
import { FEATURE_FLAGS } from '~featureFlags'
import { useAccess } from '~hooks/access/useAccess'
import { useFeatureGate } from '~hooks/access/useFeatureGate'
import { useWorkspaceScopedOrgIds } from '~hooks/useWorkspaceScopedOrgIds'
import AddContactDialog from '~pages/ContactsScreen/v2/components/AddContactDialog'
import StartWorkflowDialog from '~pages/ProcessesScreen/v2/StartWorkflowDialog'
import { type RootState } from '~redux/rootReducer'
import { lightTokens } from '~tokens/internal'
import {
  type Case2,
  type IdentityId,
  type LicenseParty,
  type LicenseTemplateSummary,
  type Process0,
  type UserId,
} from '~types'
import {
  loadResourcePermittedOrgIdentities,
  loadResourcePermittedOrgIdentitiesWithAdHoc,
} from '~utils/permissionsUtils'

import './CommandCenter.scss'

type PageItem = {
  iconName: SemanticIconName
  label: string
  description?: string
  keywords?: string[]
  onClick: () => void
}

type PageSection = {
  title: string
  items: PageItem[]
}

type QuickSearchContact = {
  id: string
  name: string
  first_name: string
  last_name: string
  image?: string | null
  emails: string[]
  phones: string[]
  addresses: string[]
  path: string
}

type QuickSearchResults = {
  contacts: QuickSearchContact[]
  cases: Case2[]
  processes: Process0[]
  licenseTemplates: LicenseTemplateSummary[]
  licenseParties: Record<string, LicenseParty[]>
}

const EMPTY_RESULTS: QuickSearchResults = {
  contacts: [],
  cases: [],
  processes: [],
  licenseTemplates: [],
  licenseParties: {},
}
const QUICK_SEARCH_DEBOUNCE_MS = 300
const QUICK_SEARCH_LIMIT = 2
const JUMP_TO_LIMIT = 6

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

const processPath = (process: Process0): string =>
  `/organizations/${process.owner.handle}/workflows/${process.code}/dashboard`

const casePath = (caseResult: Case2): string => `/cases/${caseResult.handle}`

const licenseTemplatePath = (template: LicenseTemplateSummary): string =>
  `/organizations/${template.organization_handle}/licenses-and-permits/${template.code}`

const licensePath = (templatePath: string, licenseNumber: string): string =>
  `${templatePath}/${encodeURIComponent(licenseNumber)}`

type SelectableEntry = {
  key: string
  onSelect: () => void
}

const NAV_INDEX_ATTR = 'data-command-center-index'

const normalize = (s: string): string => s.toLowerCase().trim()

const scoreItem = (item: PageItem, sectionTitle: string, query: string): number => {
  const tokens = normalize(query).split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return 0

  const label = normalize(item.label)
  const description = normalize(item.description ?? '')
  const keywords = (item.keywords ?? []).map(normalize)
  const section = normalize(sectionTitle)

  let score = 0
  for (const token of tokens) {
    if (label.startsWith(token)) score += 100
    else if (label.includes(token)) score += 80
    else if (keywords.some((k) => k.startsWith(token))) score += 50
    else if (keywords.some((k) => k.includes(token))) score += 40
    else if (description.includes(token)) score += 20
    else if (section.includes(token)) score += 10
    else return 0
  }
  return score
}

type QuickSearchCardProps = {
  iconName?: SemanticIconName
  avatar?: { name: string; image?: string | null }
  title: string
  chip?: React.ReactNode
  secondary?: React.ReactNode
  trailing?: React.ReactNode
  nested?: boolean
  disabled?: boolean
  selected?: boolean
  navIndex?: number
  onClick: () => void
  children?: React.ReactNode
}

const QuickSearchCard = ({
  iconName,
  avatar,
  title,
  chip,
  secondary,
  trailing,
  nested,
  disabled,
  selected,
  navIndex,
  onClick,
  children,
}: QuickSearchCardProps): React.JSX.Element => (
  <MenuList disablePadding className='command-center__result-card-list'>
    <MenuItem
      className='command-center__result-card'
      disabled={disabled}
      selected={selected}
      onClick={onClick}
      {...{ [NAV_INDEX_ATTR]: navIndex }}
    >
      {!nested && (
        <Box className='command-center__result-card-icon'>
          {avatar ? (
            <Avatar size='md' alt={avatar.name} src={avatar.image ?? undefined} ariaHidden />
          ) : (
            <Icon name={iconName ?? 'search'} />
          )}
        </Box>
      )}
      <Box className='command-center__result-card-body'>
        <Box className='command-center__result-card-content'>
          <Box className='command-center__result-card-header'>
            <Typography variant='subtitle2' noWrap>
              {title}
            </Typography>
            {chip}
          </Box>
          {secondary && <Box className='command-center__result-card-secondary'>{secondary}</Box>}
        </Box>
        {trailing && <Box className='command-center__result-card-trailing'>{trailing}</Box>}
        <Icon name='openInNew' fontSize='small' className='command-center__result-card-open' />
      </Box>
    </MenuItem>
    {children && <Box className='command-center__result-card-children'>{children}</Box>}
  </MenuList>
)

const SecondaryText = ({
  children,
  noWrap = true,
}: {
  children: React.ReactNode
  noWrap?: boolean
}): React.JSX.Element => (
  <Typography variant='caption' color='textSecondary' noWrap={noWrap}>
    {children}
  </Typography>
)

const contactSecondary = (contact: QuickSearchContact): React.ReactNode => {
  const email = contact.emails[0]
  const detail = email ?? contact.phones[0] ?? contact.addresses[0]
  if (!detail) return undefined
  const iconName: SemanticIconName = email
    ? 'mailOutline'
    : contact.phones[0]
      ? 'phone'
      : 'location'
  return (
    <Box className='command-center__result-card-meta'>
      <Icon name={iconName} size={14} className='command-center__result-card-meta-icon' />
      <SecondaryText>{detail}</SecondaryText>
    </Box>
  )
}

interface CommandCenterProps {
  open: boolean
  onClose: () => void
}

const CommandCenter: React.FC<CommandCenterProps> = ({ open, onClose }) => {
  const [searchValue, setSearchValue] = useState('')
  const searchRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [isProcessSelectionOpen, setIsProcessSelectionOpen] = useState(false)
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false)
  const navigate = useNavigate()

  const selectedOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )
  const orgHandle = selectedOrganization?.handle
  const isAssistantEnabled = useSelector((state: RootState) =>
    (state?.organization.emailIdentities ?? []).some((ei) => ei.assistant_enabled),
  )
  const { enabled: licensesAndPermitsEnabled } = useFeatureGate(FEATURE_FLAGS.LICENSES_AND_PERMITS)
  const { enabled: aiUsagePoliciesEnabled } = useFeatureGate(FEATURE_FLAGS.AI_USAGE_POLICIES)
  const { allowed: canWriteOrganizationSettings } = useAccess({
    permission: { resource: 'organization_settings', action: 'write' },
    targetOrganizationIdentityId: selectedOrganization?.id as IdentityId | undefined,
  })
  const { allowed: canReadInteractionCustomization } = useAccess({
    permission: { resource: 'interaction_customization', action: 'read' },
    targetOrganizationIdentityId: selectedOrganization?.id as IdentityId | undefined,
  })
  const showAIUsagePolicies =
    aiUsagePoliciesEnabled && canWriteOrganizationSettings && canReadInteractionCustomization
  const { allowed: canWriteKnowledgeBase, isSuperUser } = useAccess({
    permission: { resource: 'knowledge_base', action: 'write' },
    targetOrganizationIdentityId: selectedOrganization?.id as IdentityId | undefined,
  })
  const showKnowledgeBaseSettings = isSuperUser || canWriteKnowledgeBase
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const workspaceId = useSelector(
    (state: RootState) => state?.organization.selectedWorkspace?.id ?? null,
  )

  const [rawSearchableOrgIds, setRawSearchableOrgIds] = useState<IdentityId[]>([])
  const searchableOrgIds = useWorkspaceScopedOrgIds(rawSearchableOrgIds)
  const [searchResults, setSearchResults] = useState<QuickSearchResults>(EMPTY_RESULTS)
  const [searchLoading, setSearchLoading] = useState(false)
  const latestSearchRequestId = useRef(0)

  useEffect(() => {
    if (!open || !userId || rawSearchableOrgIds.length > 0) return
    let cancelled = false
    const load = async (): Promise<void> => {
      const [cases, contacts, processes, licenses] = await Promise.all([
        loadResourcePermittedOrgIdentitiesWithAdHoc(userId as UserId, 'cases'),
        loadResourcePermittedOrgIdentities(userId as UserId, 'contacts'),
        loadResourcePermittedOrgIdentitiesWithAdHoc(userId as UserId, 'processes'),
        loadResourcePermittedOrgIdentities(userId as UserId, 'licenses'),
      ])
      if (cancelled) return
      const orgIds = new Set<IdentityId>([
        ...(cases['read'] ?? []),
        ...(contacts['read'] ?? []),
        ...(processes['read'] ?? []),
        ...(licenses['read'] ?? []),
      ])
      setRawSearchableOrgIds(Array.from(orgIds))
    }
    load()
    return (): void => {
      cancelled = true
    }
  }, [open, userId, rawSearchableOrgIds.length])

  useEffect(() => {
    const query = searchValue.trim()
    if (!open || !userId || !query || searchableOrgIds.length === 0) {
      setSearchResults(EMPTY_RESULTS)
      setSearchLoading(false)
      return
    }
    setSearchLoading(true)
    const requestId = ++latestSearchRequestId.current
    const timeout = setTimeout(async () => {
      try {
        const response = await apiZodios.core_quickSearch({
          user_id: userId,
          query,
          organization_identity_id_list: searchableOrgIds,
          workspace_id: workspaceId ?? undefined,
          limit: QUICK_SEARCH_LIMIT,
        })
        if (latestSearchRequestId.current !== requestId) return
        setSearchResults({
          contacts: response.contacts ?? [],
          cases: response.cases ?? [],
          processes: response.processes ?? [],
          licenseTemplates: licensesAndPermitsEnabled ? (response.license_templates ?? []) : [],
          licenseParties: licensesAndPermitsEnabled ? (response.license_parties ?? {}) : {},
        })
      } catch {
        if (latestSearchRequestId.current !== requestId) return
        setSearchResults(EMPTY_RESULTS)
      } finally {
        if (latestSearchRequestId.current === requestId) setSearchLoading(false)
      }
    }, QUICK_SEARCH_DEBOUNCE_MS)
    return (): void => clearTimeout(timeout)
  }, [open, userId, searchValue, searchableOrgIds, workspaceId, licensesAndPermitsEnabled])

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        searchRef.current?.querySelector('input')?.focus()
      })
    }
  }, [open])

  const handleClose = (): void => {
    setSearchValue('')
    setSearchResults(EMPTY_RESULTS)
    setSearchLoading(false)
    setSelectedIndex(-1)
    onClose()
  }

  const handleNavigate = (path: string): void => {
    handleClose()
    navigate(path)
  }

  const handleOpenExternal = (url: string): void => {
    handleClose()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleOpenAssistant = (query?: string): void => {
    handleClose()
    navigate('/assistant', {
      state: { prefillQuery: query ?? searchValue },
    })
  }

  const quickActions = [
    {
      label: 'Open manual case',
      icon: <Icon name='add' size={14} />,
      iconBg: lightTokens.actionColors.blue,
      onClick: (): void => handleNavigate('/create-case/ViaSite'),
    },
    {
      label: 'Start workflow',
      icon: <Icon name='accountTree' size={14} />,
      iconBg: lightTokens.actionColors.teal,
      onClick: (): void => {
        handleClose()
        setIsProcessSelectionOpen(true)
      },
    },
    {
      label: 'Add contact',
      icon: <Icon name='personAdd' size={14} />,
      iconBg: lightTokens.actionColors.pink,
      onClick: (): void => {
        handleClose()
        setIsAddContactDialogOpen(true)
      },
    },
    ...(isAssistantEnabled
      ? [
          {
            label: 'Open Assistant',
            icon: <AISearchFilledIcon size={14} />,
            iconBg: lightTokens.actionColors.purple,
            onClick: (): void => handleNavigate('/assistant'),
          },
        ]
      : []),
  ]

  const defaultNavItems: { label: string; iconName: SemanticIconName; path: string }[] = [
    { label: 'Cases', iconName: 'folder', path: '/cases' },
    { label: 'Contacts', iconName: 'group', path: '/contacts' },
    { label: 'Workflows', iconName: 'process', path: '/processes' },
    ...(licensesAndPermitsEnabled
      ? [
          {
            label: 'Licenses and Permits',
            iconName: 'license' as SemanticIconName,
            path: '/licenses-and-permits',
          },
        ]
      : []),
    { label: 'Payments', iconName: 'payments', path: '/bills' },
    { label: 'Analytics', iconName: 'barChart', path: '/analytics' },
    ...(orgHandle
      ? [
          {
            label: 'Knowledge Base',
            iconName: 'menuBook' as SemanticIconName,
            path: `/organizations/${orgHandle}/sources`,
          },
          {
            label: 'Log',
            iconName: 'callLog' as SemanticIconName,
            path: `/organizations/${orgHandle}/ai-log`,
          },
          {
            label: 'Health Check',
            iconName: 'healthCheck' as SemanticIconName,
            path: `/organizations/${orgHandle}/health-check`,
          },
        ]
      : []),
  ]

  const menuStructure: PageSection[] = useMemo(
    () => [
      {
        title: 'AI Front Desk',
        items: [
          {
            iconName: 'chat' as SemanticIconName,
            label: 'Chat',
            description: 'AI-powered constituent chat interface',
            keywords: ['constituent chat', 'chatbot', 'test chat', 'preview', 'try it'],
            onClick: (): void => {
              if (orgHandle) handleNavigate(`/organizations/${orgHandle}/chat?test`)
            },
          },
          {
            iconName: 'search' as SemanticIconName,
            label: 'Search',
            description: 'Query your organization data with AI',
            keywords: ['ai search', 'semantic search', 'test search', 'preview'],
            onClick: (): void => {
              if (orgHandle) handleNavigate(`/organizations/${orgHandle}/search?test`)
            },
          },
          {
            iconName: 'menuBook' as SemanticIconName,
            label: 'Knowledge Base',
            description: 'Manage the content your AI agents use to answer questions.',
            keywords: [
              'sources',
              'content',
              'website',
              'crawl',
              'documents',
              'upload',
              'urls',
              'faq',
              'training data',
            ],
            onClick: (): void => {
              if (orgHandle) handleNavigate(`/organizations/${orgHandle}/sources`)
            },
          },
          {
            iconName: 'edit' as SemanticIconName,
            label: 'Customize',
            description: 'Customize your organization data',
            keywords: [
              'chat design',
              'chat name',
              'rename chat',
              'assistant name',
              'branding',
              'colors',
              'logo',
              'greeting',
              'welcome message',
              'appearance',
              'theme',
              'tone',
              'persona',
              'suggested questions',
              'prompts',
            ],
            onClick: (): void => {
              if (orgHandle) handleNavigate(`/organizations/${orgHandle}/ai-customize`)
            },
          },
          {
            iconName: 'sensors' as SemanticIconName,
            label: 'Channels',
            description: 'Publish your organization AI channels',
            keywords: [
              'publish',
              'embed',
              'widget',
              'website chat',
              'phone',
              'voice',
              'sms',
              'email',
              'deploy',
              'go live',
            ],
            onClick: (): void => handleNavigate('/settings-hub/channels'),
          },
          ...(showKnowledgeBaseSettings
            ? [
                {
                  iconName: 'globe' as SemanticIconName,
                  label: 'GIS Layers',
                  description: 'Map layers your AI assistant can use for location questions',
                  keywords: [
                    'gis',
                    'map',
                    'maps',
                    'layers',
                    'geometry',
                    'geojson',
                    'shapefile',
                    'parcels',
                    'zoning',
                    'location',
                    'address',
                  ],
                  onClick: (): void => handleNavigate('/settings-hub/gis-layers'),
                },
              ]
            : []),
          {
            iconName: 'healthCheck' as SemanticIconName,
            label: 'Health Check',
            description: 'Test your organization data',
            keywords: ['test', 'evaluate', 'quality', 'accuracy', 'qa', 'questions'],
            onClick: (): void => {
              if (orgHandle) handleNavigate(`/organizations/${orgHandle}/health-check`)
            },
          },
          {
            iconName: 'barChart' as SemanticIconName,
            label: 'AI Analytics',
            description: 'View AI usage and performance metrics',
            keywords: [
              'interactions',
              'usage',
              'metrics',
              'reports',
              'performance',
              'dashboard',
              'volume',
            ],
            onClick: (): void => {
              if (orgHandle) handleNavigate(`/organizations/${orgHandle}/analytics`)
            },
          },
        ],
      },
      {
        title: 'CRM & Communications',
        items: [
          {
            iconName: 'folder',
            label: 'Cases',
            description: 'Manage constituent cases and communications',
            keywords: ['tickets', 'requests', 'inbox', 'support', 'submissions'],
            onClick: () => handleNavigate('/cases'),
          },
          {
            iconName: 'group',
            label: 'Contacts',
            description: 'View and manage your constituent contacts',
            keywords: ['constituents', 'residents', 'people', 'customers', 'directory', 'import'],
            onClick: () => handleNavigate('/contacts'),
          },
          {
            iconName: 'campaign',
            label: 'Outbound Communications',
            description: 'Send communications to multiple contacts',
            keywords: [
              'mass email',
              'mass text',
              'bulk',
              'newsletter',
              'broadcast',
              'announcement',
              'campaign',
              'sms blast',
            ],
            onClick: () => handleNavigate(`/mass-communication/${orgHandle ?? ''}`),
          },
        ],
      },
      {
        title: 'Workflows',
        items: [
          {
            iconName: 'process',
            label: 'Workflows Library',
            description: 'Browse and manage workflows',
            keywords: ['forms', 'processes', 'applications', 'intake'],
            onClick: () => handleNavigate('/processes'),
          },
          {
            iconName: 'draw',
            label: 'Create Workflow',
            description: 'Create a new workflow',
            keywords: ['new form', 'build form', 'new process', 'form builder'],
            onClick: () => handleNavigate('/create-workflow'),
          },
        ],
      },
      ...(licensesAndPermitsEnabled
        ? [
            {
              title: 'Licenses and Permits',
              items: [
                {
                  iconName: 'license' as SemanticIconName,
                  label: 'Licenses and Permits',
                  description: 'View and manage licenses and permits',
                  keywords: ['license', 'permit', 'applications', 'renewals', 'expiration'],
                  onClick: () => handleNavigate('/licenses-and-permits'),
                },
              ],
            },
          ]
        : []),
      {
        title: 'Payments',
        items: [
          {
            iconName: 'savings',
            label: 'Payments',
            description: 'Manage bills and invoices',
            keywords: ['bills', 'invoices', 'fees', 'receipts', 'refunds'],
            onClick: () => handleNavigate('/bills'),
          },
          {
            iconName: 'payments',
            label: 'Collect Payment',
            description: 'Process new payments',
            keywords: ['charge', 'credit card', 'take payment', 'pay', 'checkout'],
            onClick: () => handleNavigate('/collect-payment'),
          },
        ],
      },
      {
        title: 'Administration',
        items: [
          {
            iconName: 'settings',
            label: 'General Settings',
            description: 'Configure organization settings',
            keywords: ['organization', 'name', 'address', 'hours', 'timezone', 'details'],
            onClick: () => handleNavigate('/settings-hub/details'),
          },
          {
            iconName: 'security',
            label: 'Permissions',
            description: 'Manage user roles and permissions',
            keywords: ['users', 'roles', 'invite', 'access', 'team', 'staff', 'admin'],
            onClick: (): void => handleNavigate('/settings-hub/users'),
          },
          {
            iconName: 'payments',
            label: 'Payment Settings',
            description: 'Configure payment processing',
            keywords: ['stripe', 'processor', 'bank', 'payouts', 'fees'],
            onClick: () => handleNavigate('/settings-hub/payments'),
          },
          {
            iconName: 'label',
            label: 'Tags',
            description: 'Manage organizational tags',
            keywords: ['labels', 'categories'],
            onClick: () => handleNavigate('/settings-hub/tags'),
          },
          {
            iconName: 'description',
            label: 'Form Letters',
            description: 'Create and manage form letter templates',
            keywords: ['templates', 'letters', 'canned responses', 'email templates'],
            onClick: () => handleNavigate('/settings-hub/form-letters'),
          },
          {
            iconName: 'key',
            label: 'SSO Setup',
            description: 'Configure single sign-on',
            keywords: ['single sign-on', 'saml', 'okta', 'azure', 'login', 'authentication'],
            onClick: () => handleNavigate('/settings-hub/sso'),
          },
          {
            iconName: 'email',
            label: 'Email Domains',
            description: 'Verify domains for sending email',
            keywords: ['dkim', 'spf', 'dns', 'sender', 'from address', 'verify domain'],
            onClick: () => handleNavigate('/settings-hub/email-domains'),
          },
          ...(showKnowledgeBaseSettings
            ? [
                {
                  iconName: 'phone' as SemanticIconName,
                  label: 'Transfer Lines',
                  description: 'Phone numbers your AI assistant can transfer callers to',
                  keywords: [
                    'transfer',
                    'phone',
                    'phone numbers',
                    'call transfer',
                    'warm transfer',
                    'departments',
                    'directory',
                    'extensions',
                    'forwarding',
                    'voice',
                  ],
                  onClick: (): void => handleNavigate('/settings-hub/transfer-lines'),
                },
              ]
            : []),
          {
            iconName: 'manageHistory',
            label: 'Data Retention',
            description: 'Manage data retention policies',
            keywords: ['delete', 'purge', 'privacy', 'records', 'retention window', 'compliance'],
            onClick: () => handleNavigate('/settings-hub/data-retention'),
          },
          ...(showAIUsagePolicies
            ? [
                {
                  iconName: 'security' as SemanticIconName,
                  label: 'AI Guardrails',
                  description: 'Configure how AI features may be used',
                  keywords: ['guardrails', 'restrictions', 'compliance', 'governance'],
                  onClick: () => handleNavigate('/settings-hub/ai-guardrails'),
                },
              ]
            : []),
        ],
      },
      {
        title: 'Support',
        items: [
          {
            iconName: 'newReleases',
            label: "What's New",
            description: 'See the latest updates and improvements',
            keywords: ['changelog', 'release notes', 'updates'],
            onClick: () => handleNavigate('/changelog'),
          },
          {
            iconName: 'call',
            label: 'File Support Case',
            description: 'Get help from our support team',
            keywords: ['help', 'contact support', 'bug', 'report issue'],
            onClick: () =>
              handleOpenExternal(
                `${BASE_URL_WITH_REACT}/organizations/polimorphic/workflows/file-a-support-case/apply`,
              ),
          },
          {
            iconName: 'help',
            label: 'Help Docs',
            description: 'Browse our documentation and guides',
            keywords: ['documentation', 'guides', 'how to', 'tutorial'],
            onClick: () =>
              handleOpenExternal(
                'https://polimorphic.notion.site/Help-Support-361e2563f49f46f98bc20d46074d3d2d',
              ),
          },
        ],
      },
      {
        title: 'Account Settings',
        items: [
          {
            iconName: 'manageAccounts',
            label: 'Account Settings',
            description: 'Manage your user profile and settings',
            keywords: ['profile', 'password', 'name', 'email', 'notifications'],
            onClick: () => handleNavigate('/account'),
          },
          {
            iconName: 'factCheck',
            label: 'Terms of Use',
            description: 'View our terms of service',
            onClick: () => handleOpenExternal('https://www.polimorphic.com/terms-of-use'),
          },
          {
            iconName: 'security',
            label: 'Privacy Policy',
            description: 'View our privacy policy',
            onClick: () => handleOpenExternal('https://www.polimorphic.com/privacy-policy'),
          },
        ],
      },
    ],
    [
      orgHandle,
      isAssistantEnabled,
      licensesAndPermitsEnabled,
      showAIUsagePolicies,
      showKnowledgeBaseSettings,
    ],
  )

  const hasSearchValue = searchValue.trim().length > 0
  const hasQuickSearchResults =
    searchResults.cases.length > 0 ||
    searchResults.contacts.length > 0 ||
    searchResults.processes.length > 0 ||
    searchResults.licenseTemplates.length > 0

  const jumpToResults = useMemo(() => {
    if (!hasSearchValue) return []
    return menuStructure
      .flatMap((section) =>
        section.items.map((item) => ({
          key: `${section.title}-${item.label}`,
          item,
          score: scoreItem(item, section.title, searchValue),
        })),
      )
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score) // Array.sort is stable, so ties keep menu order
      .slice(0, JUMP_TO_LIMIT)
  }, [hasSearchValue, searchValue, menuStructure])

  const selectableEntries: SelectableEntry[] = useMemo(() => {
    if (!hasSearchValue) {
      return [
        ...quickActions.map((action) => ({
          key: `quick-action:${action.label}`,
          onSelect: action.onClick,
        })),
        ...defaultNavItems.map((item) => ({
          key: `nav:${item.label}`,
          onSelect: () => handleNavigate(item.path),
        })),
      ]
    }

    const entries: SelectableEntry[] = []
    if (isAssistantEnabled) {
      entries.push({ key: 'assistant', onSelect: () => handleOpenAssistant() })
    }
    jumpToResults.forEach(({ key, item }) => {
      entries.push({ key: `jump:${key}`, onSelect: item.onClick })
    })
    searchResults.contacts.slice(0, QUICK_SEARCH_LIMIT).forEach((contact) => {
      if (contact.path) {
        const path = contact.path
        entries.push({ key: `contact:${contact.id}`, onSelect: () => handleNavigate(path) })
      }
    })
    searchResults.processes.slice(0, QUICK_SEARCH_LIMIT).forEach((process) => {
      entries.push({
        key: `process:${process.id}`,
        onSelect: () => handleNavigate(processPath(process)),
      })
    })
    searchResults.cases.slice(0, QUICK_SEARCH_LIMIT).forEach((caseResult) => {
      entries.push({
        key: `case:${caseResult.id}`,
        onSelect: () => handleNavigate(casePath(caseResult)),
      })
    })
    searchResults.licenseTemplates.slice(0, QUICK_SEARCH_LIMIT).forEach((template) => {
      const templatePath = licenseTemplatePath(template)
      entries.push({
        key: `license-template:${template.id}`,
        onSelect: () => handleNavigate(templatePath),
      })
      template.licenses.forEach((license) => {
        entries.push({
          key: `license:${template.id}:${license.id}`,
          onSelect: () => handleNavigate(licensePath(templatePath, license.number)),
        })
      })
    })
    return entries
  }, [
    hasSearchValue,
    isAssistantEnabled,
    searchValue,
    jumpToResults,
    searchResults,
    quickActions,
    defaultNavItems,
  ])

  const entryIndexByKey = useMemo(
    () => new Map(selectableEntries.map((entry, index) => [entry.key, index])),
    [selectableEntries],
  )

  // Results can shrink between renders before the reset effect runs.
  const activeIndex = selectedIndex < selectableEntries.length ? selectedIndex : -1

  const navProps = (key: string): { selected: boolean; [NAV_INDEX_ATTR]?: number } => {
    const index = entryIndexByKey.get(key)
    return { selected: index !== undefined && index === activeIndex, [NAV_INDEX_ATTR]: index }
  }

  const cardNavProps = (key: string): { selected: boolean; navIndex?: number } => {
    const index = entryIndexByKey.get(key)
    return { selected: index !== undefined && index === activeIndex, navIndex: index }
  }

  useEffect(() => {
    setSelectedIndex(-1)
  }, [searchValue, searchResults])

  useEffect(() => {
    if (activeIndex < 0) return
    contentRef.current
      ?.querySelector<HTMLElement>(`[${NAV_INDEX_ATTR}="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const moveSelection = (direction: 1 | -1): void => {
    const count = selectableEntries.length
    if (count === 0) return
    setSelectedIndex((prev) => {
      const current = prev < count ? prev : -1
      if (direction === 1) return current >= count - 1 ? 0 : current + 1
      return current <= 0 ? -1 : current - 1
    })
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.nativeEvent.isComposing) return
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        moveSelection(1)
        break
      case 'ArrowUp':
        e.preventDefault()
        moveSelection(-1)
        break
      case 'Tab':
        e.preventDefault()
        moveSelection(e.shiftKey ? -1 : 1)
        break
      case 'Enter': {
        const entry = activeIndex >= 0 ? selectableEntries[activeIndex] : undefined
        if (entry) {
          e.preventDefault()
          entry.onSelect()
        } else if (searchValue.trim() && isAssistantEnabled) {
          handleOpenAssistant()
        }
        break
      }
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        disableAutoFocus
        disableEnforceFocus
        classes={{ paper: 'command-center' }}
      >
        <DialogTitle className='command-center__header'>
          <Search
            ref={searchRef}
            autoFocus
            width='full'
            placeholder='Search, ask a question, or quick create'
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <IconButton size='small' onClick={handleClose}>
            <Icon fontSize='small' name='close' />
          </IconButton>
        </DialogTitle>

        <DialogContent ref={contentRef} className='command-center__content'>
          {hasSearchValue ? (
            <>
              {isAssistantEnabled && (
                <MenuList disablePadding>
                  <MenuItem
                    className='command-center__menu-item command-center__ai-row'
                    onClick={() => handleOpenAssistant()}
                    {...navProps('assistant')}
                  >
                    <ListItemIcon>
                      <AISearchFilledIcon size={20} />
                    </ListItemIcon>
                    <ListItemText className='command-center__ai-row-text'>
                      <Typography variant='body2'>{searchValue}</Typography>
                    </ListItemText>
                    <Button
                      size='small'
                      variant='outlined'
                      className='command-center__ai-row-button'
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenAssistant()
                      }}
                      startIcon={
                        <span
                          className='command-center__action-icon'
                          style={{ backgroundColor: lightTokens.actionColors.purple }}
                        >
                          <AISearchFilledIcon size={14} />
                        </span>
                      }
                    >
                      Open Assistant
                    </Button>
                  </MenuItem>
                </MenuList>
              )}

              {jumpToResults.length > 0 && (
                <Box className='command-center__section'>
                  <Typography variant='subtitle2' color='textSecondary'>
                    Jump to
                  </Typography>
                  <MenuList className='command-center__nav-list'>
                    {jumpToResults.map(({ key, item }) => (
                      <MenuItem
                        key={key}
                        className='command-center__menu-item'
                        onClick={item.onClick}
                        {...navProps(`jump:${key}`)}
                      >
                        <ListItemIcon>
                          <Icon name={item.iconName} fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>
                          <Typography variant='body2'>{item.label}</Typography>
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Box>
              )}

              {(hasQuickSearchResults || searchLoading) && (
                <Box className='command-center__section'>
                  <Box className='command-center__results-header' sx={{ height: 20 }}>
                    {searchLoading && <CircularProgress size={14} />}
                  </Box>

                  {searchResults.contacts.length > 0 && (
                    <Box className='command-center__result-group'>
                      <Typography variant='body2' color='textSecondary'>
                        Contacts
                      </Typography>
                      <Box className='command-center__result-cards'>
                        {searchResults.contacts.slice(0, QUICK_SEARCH_LIMIT).map((contact) => {
                          const displayName =
                            [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
                            contact.name
                          return (
                            <QuickSearchCard
                              key={contact.id}
                              avatar={{ name: displayName, image: contact.image }}
                              title={displayName}
                              secondary={contactSecondary(contact)}
                              disabled={!contact.path}
                              {...cardNavProps(`contact:${contact.id}`)}
                              onClick={() => {
                                if (contact.path) {
                                  handleNavigate(contact.path)
                                }
                              }}
                            />
                          )
                        })}
                      </Box>
                    </Box>
                  )}

                  {searchResults.processes.length > 0 && (
                    <Box className='command-center__result-group'>
                      <Typography variant='body2' color='textSecondary'>
                        Workflows
                      </Typography>
                      <Box className='command-center__result-cards'>
                        {searchResults.processes.slice(0, QUICK_SEARCH_LIMIT).map((process) => (
                          <QuickSearchCard
                            key={process.id}
                            iconName='process'
                            title={process.name}
                            chip={
                              ['live', 'draft'].includes(process.status) ? (
                                <KnownChip type={process.status as KnownChipType} size='small' />
                              ) : undefined
                            }
                            secondary={
                              process.owner.name ? (
                                <Box className='command-center__result-card-meta'>
                                  <SecondaryText>{process.owner.name}</SecondaryText>
                                </Box>
                              ) : undefined
                            }
                            {...cardNavProps(`process:${process.id}`)}
                            onClick={() => handleNavigate(processPath(process))}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {searchResults.cases.length > 0 && (
                    <Box className='command-center__result-group'>
                      <Typography variant='body2' color='textSecondary'>
                        Cases
                      </Typography>
                      <Box className='command-center__result-cards'>
                        {searchResults.cases.slice(0, QUICK_SEARCH_LIMIT).map((caseResult) => (
                          <QuickSearchCard
                            key={caseResult.id}
                            iconName='folder'
                            title={caseResult.title}
                            chip={
                              caseResult.status &&
                              ['open', 'closed', 'deleted', 'hidden', 'unsubmitted'].includes(
                                caseResult.status.toLowerCase(),
                              ) ? (
                                <StatusChip
                                  config={
                                    caseStatusConfig[
                                      caseResult.status.toLowerCase() as StatusChipType
                                    ]
                                  }
                                  size='small'
                                />
                              ) : undefined
                            }
                            secondary={
                              <>
                                {caseResult.requester?.name && (
                                  <>
                                    <Box className='command-center__result-card-meta'>
                                      <SecondaryText>Requestor:</SecondaryText>
                                      <Avatar
                                        size='xs'
                                        identity={caseResult.requester}
                                        ariaHidden
                                      />
                                      <SecondaryText>{caseResult.requester.name}</SecondaryText>
                                    </Box>
                                    <Box className='command-center__result-card-divider' />
                                  </>
                                )}
                                <Box className='command-center__result-card-meta'>
                                  <SecondaryText>
                                    Requested On: {formatDate(caseResult.created_time)}
                                  </SecondaryText>
                                </Box>
                              </>
                            }
                            {...cardNavProps(`case:${caseResult.id}`)}
                            onClick={() => handleNavigate(casePath(caseResult))}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {searchResults.licenseTemplates.length > 0 && (
                    <Box className='command-center__result-group'>
                      <Typography variant='body2' color='textSecondary'>
                        Licenses and Permits
                      </Typography>
                      <Box className='command-center__result-cards'>
                        {searchResults.licenseTemplates
                          .slice(0, QUICK_SEARCH_LIMIT)
                          .map((template) => {
                            const templatePath = licenseTemplatePath(template)
                            return (
                              <QuickSearchCard
                                key={template.id}
                                iconName='license'
                                title={template.name}
                                secondary={
                                  <Box className='command-center__result-card-meta'>
                                    <SecondaryText>
                                      Active: {template.active.toLocaleString()}
                                    </SecondaryText>
                                    <Box className='command-center__result-card-divider' />
                                    <SecondaryText>
                                      Total: {template.total.toLocaleString()}
                                    </SecondaryText>
                                  </Box>
                                }
                                {...cardNavProps(`license-template:${template.id}`)}
                                onClick={() => handleNavigate(templatePath)}
                              >
                                {template.licenses.map((license) => {
                                  const parties = searchResults.licenseParties[license.id] ?? []
                                  return (
                                    <QuickSearchCard
                                      key={license.id}
                                      nested
                                      title={license.number}
                                      secondary={
                                        parties.length > 0 && (
                                          <Box className='command-center__result-card-meta'>
                                            <SecondaryText>Parties:</SecondaryText>
                                            <ContactList contacts={parties} avatarSize='xs' />
                                          </Box>
                                        )
                                      }
                                      trailing={
                                        <LicenseStatusChip status={license.status} size='small' />
                                      }
                                      {...cardNavProps(`license:${template.id}:${license.id}`)}
                                      onClick={() =>
                                        handleNavigate(licensePath(templatePath, license.number))
                                      }
                                    />
                                  )
                                })}
                              </QuickSearchCard>
                            )
                          })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </>
          ) : (
            <>
              {/* Quick Actions */}
              <Box className='command-center__section'>
                <Typography variant='subtitle2' color='textSecondary'>
                  Quick actions
                </Typography>
                <Box className='command-center__quick-actions'>
                  {quickActions.map((action) => {
                    const { selected, ...indexAttr } = navProps(`quick-action:${action.label}`)
                    return (
                      <Button
                        key={action.label}
                        size='small'
                        variant='outlined'
                        className={selected ? 'command-center__quick-action--selected' : undefined}
                        onClick={action.onClick}
                        {...indexAttr}
                        startIcon={
                          <span
                            className='command-center__action-icon'
                            style={{ backgroundColor: action.iconBg }}
                          >
                            {action.icon}
                          </span>
                        }
                      >
                        {action.label}
                      </Button>
                    )
                  })}
                </Box>
              </Box>

              {/* Jump To */}
              <Box className='command-center__section'>
                <Typography variant='subtitle2' color='textSecondary'>
                  Jump to
                </Typography>
                <MenuList className='command-center__nav-list'>
                  {defaultNavItems.map((item) => (
                    <MenuItem
                      key={item.label}
                      className='command-center__menu-item'
                      onClick={() => handleNavigate(item.path)}
                      {...navProps(`nav:${item.label}`)}
                    >
                      <ListItemIcon>
                        <Icon name={item.iconName} fontSize='small' />
                      </ListItemIcon>
                      <ListItemText>
                        <Typography variant='body2'>{item.label}</Typography>
                      </ListItemText>
                    </MenuItem>
                  ))}
                </MenuList>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <StartWorkflowDialog
        open={isProcessSelectionOpen}
        onClose={() => setIsProcessSelectionOpen(false)}
        requesterId={null}
        owners={undefined}
      />

      <AddContactDialog
        open={isAddContactDialogOpen}
        onClose={() => setIsAddContactDialogOpen(false)}
      />
    </>
  )
}

export default CommandCenter
